// Minimal SSE reader for OpenAI-compatible chat-completions streams.
//
// The Hermes gateway returns server-sent events in OpenAI's shape:
//
//   data: {"choices":[{"delta":{"content":"hello"}}]}
//   data: {"choices":[{"delta":{"content":" world"}}]}
//   data: [DONE]
//
// `readChatStream` calls `onDelta(content)` for each non-empty content delta
// and resolves when the upstream sends `[DONE]` or the reader closes. JSON
// parse errors on individual events are swallowed - a malformed event must
// not abort the whole stream.

interface OpenAIStreamChunk {
  choices?: Array<{ delta?: { content?: string } }>;
}

export async function readChatStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onDelta: (text: string) => void,
): Promise<void> {
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      flushBuffer(buffer, onDelta);
      return;
    }
    buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const event = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      if (handleEvent(event, onDelta) === "done") return;
      sep = buffer.indexOf("\n\n");
    }
  }
}

function flushBuffer(buf: string, onDelta: (text: string) => void): void {
  const trimmed = buf.trim();
  if (trimmed.length === 0) return;
  handleEvent(trimmed, onDelta);
}

function handleEvent(event: string, onDelta: (text: string) => void): "done" | "ok" {
  for (const rawLine of event.split("\n")) {
    const line = rawLine.trimEnd();
    if (!line.startsWith("data:")) continue;
    const payload = line.slice(5).trimStart();
    if (payload === "[DONE]") return "done";
    if (payload.length === 0) continue;
    try {
      const parsed = JSON.parse(payload) as OpenAIStreamChunk;
      const delta = parsed.choices?.[0]?.delta?.content;
      if (typeof delta === "string" && delta.length > 0) onDelta(delta);
    } catch {
      // Malformed event - skip, do not abort the stream.
    }
  }
  return "ok";
}
